export type AssignAndFillConfettiOrigin = {
    readonly x: number;
    readonly y: number;
};
export type AssignAndFillConfettiOriginSource =
    | AssignAndFillConfettiOrigin
    | HTMLElement
    | null;

const ASSIGN_AND_FILL_CONFETTI_COLORS = [
    '#38bdf8',
    '#34d399',
    '#fbbf24',
    '#fb7185',
    '#a78bfa',
    '#f97316',
] as const;

const ASSIGN_AND_FILL_CONFETTI_DURATION_MS = 2600;
const ASSIGN_AND_FILL_CONFETTI_PARTICLES_PER_SIDE = 45;
const ASSIGN_AND_FILL_CONFETTI_SHAPES = [
    'rectangle',
    'dot',
    'streamer',
] as const;

type AssignAndFillConfettiShape =
    (typeof ASSIGN_AND_FILL_CONFETTI_SHAPES)[number];

type AssignAndFillConfettiSide = 'left' | 'right';

type AssignAndFillConfettiParticleSpec = {
    readonly burstX: number;
    readonly burstY: number;
    readonly color: string;
    readonly delayMs: number;
    readonly durationMs: number;
    readonly finalX: number;
    readonly finalY: number;
    readonly height: number;
    readonly midRotation: number;
    readonly originX: number;
    readonly originY: number;
    readonly peakX: number;
    readonly peakY: number;
    readonly shape: AssignAndFillConfettiShape;
    readonly startRotation: number;
    readonly width: number;
};

type AssignAndFillConfettiShapeConfig = {
    readonly borderRadius: string;
    readonly heightRange: readonly [number, number];
    readonly widthRange: readonly [number, number];
};

const ASSIGN_AND_FILL_CONFETTI_SHAPE_CONFIGS: Record<
    AssignAndFillConfettiShape,
    AssignAndFillConfettiShapeConfig
> = {
    dot: {
        borderRadius: '9999px',
        heightRange: [6, 9],
        widthRange: [6, 9],
    },
    rectangle: {
        borderRadius: '2px',
        heightRange: [9, 14],
        widthRange: [6, 9],
    },
    streamer: {
        borderRadius: '9999px',
        heightRange: [14, 22],
        widthRange: [3, 5],
    },
};

export function getAssignAndFillConfettiOrigin(
    element: HTMLElement,
): AssignAndFillConfettiOrigin {
    const rect = element.getBoundingClientRect();
    return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
    };
}

export function triggerAssignAndFillConfetti(
    _originSource: AssignAndFillConfettiOriginSource,
): Promise<void> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.resolve();
    }

    if (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
        return Promise.resolve();
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    if (viewportWidth <= 0 || viewportHeight <= 0) {
        return Promise.resolve();
    }

    if (
        typeof Element === 'undefined' ||
        typeof Element.prototype.animate !== 'function'
    ) {
        return Promise.resolve();
    }

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.inset = '0';
    container.style.pointerEvents = 'none';
    container.style.zIndex = '60';
    document.body.appendChild(container);

    const animationFinishedPromises: Array<Promise<Animation | undefined>> = [];

    try {
        const sides: ReadonlyArray<AssignAndFillConfettiSide> = [
            'left',
            'right',
        ];
        for (const side of sides) {
            for (
                let index = 0;
                index < ASSIGN_AND_FILL_CONFETTI_PARTICLES_PER_SIDE;
                index++
            ) {
                const particleSpec = createAssignAndFillConfettiParticleSpec(
                    index,
                    side,
                    viewportWidth,
                    viewportHeight,
                );
                const particle =
                    createAssignAndFillConfettiParticleElement(particleSpec);
                container.appendChild(particle);

                const animation = particle.animate(
                    [
                        {
                            offset: 0,
                            opacity: 1,
                            transform:
                                'translate(-50%, -50%) translate(0, 0) ' +
                                `rotate(${particleSpec.startRotation}deg) scale(0.6)`,
                        },
                        {
                            offset: 0.16,
                            opacity: 1,
                            transform:
                                'translate(-50%, -50%) ' +
                                `translate(${particleSpec.burstX}px, ${particleSpec.burstY}px) ` +
                                `rotate(${particleSpec.midRotation}deg) scale(1)`,
                        },
                        {
                            offset: 0.5,
                            opacity: 0.96,
                            transform:
                                'translate(-50%, -50%) ' +
                                `translate(${particleSpec.peakX}px, ${particleSpec.peakY}px) ` +
                                `rotate(${particleSpec.midRotation * 1.4}deg) ` +
                                'scale(1)',
                        },
                        {
                            offset: 1,
                            opacity: 0,
                            transform:
                                'translate(-50%, -50%) ' +
                                `translate(${particleSpec.finalX}px, ${particleSpec.finalY}px) ` +
                                `rotate(${particleSpec.midRotation * 1.9}deg) ` +
                                'scale(0.82)',
                        },
                    ],
                    {
                        delay: particleSpec.delayMs,
                        duration: particleSpec.durationMs,
                        easing: 'cubic-bezier(0.16, 0.74, 0.3, 1)',
                        fill: 'forwards',
                    },
                );
                animationFinishedPromises.push(
                    animation.finished.catch(() => undefined),
                );
            }
        }
    } catch (error) {
        container.remove();
        throw error;
    }

    return Promise.all(animationFinishedPromises)
        .then(
            () => undefined,
            () => undefined,
        )
        .finally(() => {
            container.remove();
        });
}

function createAssignAndFillConfettiParticleSpec(
    index: number,
    side: AssignAndFillConfettiSide,
    viewportWidth: number,
    viewportHeight: number,
): AssignAndFillConfettiParticleSpec {
    const color =
        ASSIGN_AND_FILL_CONFETTI_COLORS[
            index % ASSIGN_AND_FILL_CONFETTI_COLORS.length
        ] ?? ASSIGN_AND_FILL_CONFETTI_COLORS[0];
    const shape =
        ASSIGN_AND_FILL_CONFETTI_SHAPES[
            index % ASSIGN_AND_FILL_CONFETTI_SHAPES.length
        ] ?? ASSIGN_AND_FILL_CONFETTI_SHAPES[0];

    // Two confetti cannons anchored just inside the bottom corners, each
    // firing up and across toward the middle of the screen.
    const horizontalSign = side === 'left' ? 1 : -1;
    const originX = side === 'left' ? 0 : viewportWidth;
    const originY =
        viewportHeight * randomAssignAndFillConfettiValue(0.78, 0.9);

    // Aim between ~25deg and ~70deg above horizontal, pointing inward.
    const launchAngle = randomAssignAndFillConfettiValue(
        (25 * Math.PI) / 180,
        (70 * Math.PI) / 180,
    );
    const speed = viewportWidth * randomAssignAndFillConfettiValue(0.55, 1.05);
    const velocityX = horizontalSign * Math.cos(launchAngle) * speed;
    const velocityY = -Math.sin(launchAngle) * speed;
    const gravity = viewportHeight * randomAssignAndFillConfettiValue(1.6, 2.2);

    const positionAt = (
        progress: number,
    ): { readonly dx: number; readonly dy: number } => ({
        dx: velocityX * progress,
        dy: velocityY * progress + 0.5 * gravity * progress * progress,
    });

    const burst = positionAt(0.16);
    const peak = positionAt(0.5);
    const final = positionAt(1);

    const startRotation = randomAssignAndFillConfettiValue(-45, 45);
    const midRotation =
        startRotation +
        randomAssignAndFillConfettiSign() *
            randomAssignAndFillConfettiValue(160, 340);
    const shapeConfig = ASSIGN_AND_FILL_CONFETTI_SHAPE_CONFIGS[shape];

    return {
        burstX: burst.dx,
        burstY: burst.dy,
        color,
        delayMs: randomAssignAndFillConfettiValue(0, 220),
        durationMs:
            ASSIGN_AND_FILL_CONFETTI_DURATION_MS +
            randomAssignAndFillConfettiValue(220, 620),
        finalX: final.dx,
        finalY: final.dy,
        height: randomAssignAndFillConfettiRange(shapeConfig.heightRange),
        midRotation,
        originX,
        originY,
        peakX: peak.dx,
        peakY: peak.dy,
        shape,
        startRotation,
        width: randomAssignAndFillConfettiRange(shapeConfig.widthRange),
    };
}

function createAssignAndFillConfettiParticleElement(
    particleSpec: AssignAndFillConfettiParticleSpec,
): HTMLSpanElement {
    const particle = document.createElement('span');
    particle.style.position = 'fixed';
    particle.style.left = `${particleSpec.originX}px`;
    particle.style.top = `${particleSpec.originY}px`;
    particle.style.display = 'block';
    particle.style.width = `${particleSpec.width}px`;
    particle.style.height = `${particleSpec.height}px`;
    particle.style.backgroundColor = particleSpec.color;
    particle.style.borderRadius =
        ASSIGN_AND_FILL_CONFETTI_SHAPE_CONFIGS[particleSpec.shape].borderRadius;
    particle.style.boxShadow = 'none';
    particle.style.willChange = 'transform, opacity';

    return particle;
}

function randomAssignAndFillConfettiValue(min: number, max: number): number {
    return min + Math.random() * (max - min);
}

function randomAssignAndFillConfettiRange(
    range: readonly [number, number],
): number {
    return randomAssignAndFillConfettiValue(range[0], range[1]);
}

function randomAssignAndFillConfettiSign(): number {
    return Math.random() > 0.5 ? 1 : -1;
}
